import type { OnlineEventSpeakerViewDto, WebSpeakerPatchItemDto } from '@/api/webs'
import type { SpeakerEditInput } from '@/hooks/useOnlineEventSpeakers'
import { SpeakerAddPanel } from '../SpeakerAddPanel'
import { SpeakerEditPanel } from '../SpeakerEditPanel'
import { DeleteSpeakerModal } from '../modals/DeleteSpeakerModal'
import { useEffect } from 'preact/hooks'
import { aiChatSuppressed } from '@/state/aiChat'

interface SpeakerEditorsProps {
  workspaceId: string
  webId: string
  campaignId: string
  showAddParticipant: boolean
  setShowAddParticipant: (v: boolean) => void
  editingSpeaker: OnlineEventSpeakerViewDto | null
  showEditModal: boolean
  setShowEditModal: (v: boolean) => void
  deleteTarget: OnlineEventSpeakerViewDto | null
  setDeleteTarget: (v: OnlineEventSpeakerViewDto | null) => void
  deletingParticipant: boolean
  addSpeakerAndPersist: (spk: WebSpeakerPatchItemDto) => Promise<void>
  saveEditedSpeaker: (upd: SpeakerEditInput) => Promise<void>
  deleteSpeakerAndPersist: (contactId: string) => Promise<boolean>
}

/** Hosts the add/edit speaker side panels plus the delete confirmation. */
export function SpeakerEditors({
  workspaceId, webId, campaignId,
  showAddParticipant, setShowAddParticipant,
  editingSpeaker, showEditModal, setShowEditModal,
  deleteTarget, setDeleteTarget, deletingParticipant,
  addSpeakerAndPersist, saveEditedSpeaker, deleteSpeakerAndPersist,
}: SpeakerEditorsProps) {
  // Hide the global Webster launcher while a speaker side panel is open (it would otherwise sit under the panel and be hard to reach).
  useEffect(() => {
    aiChatSuppressed.value = showAddParticipant || showEditModal
    return () => { aiChatSuppressed.value = false }
  }, [showAddParticipant, showEditModal])

  return (
    <>
      {/* Mount each panel only while open so it fully tears down on close: a fresh useAIForm/state
          every time avoids stale state carrying over between back-to-back adds. */}
      {showAddParticipant && (
        <SpeakerAddPanel
          open={showAddParticipant}
          onClose={() => setShowAddParticipant(false)}
          onAdd={async (spk) => { await addSpeakerAndPersist(spk) }}
          workspaceId={workspaceId} webId={webId} campaignId={campaignId}
        />
      )}
      {showEditModal && editingSpeaker && (
        <SpeakerEditPanel
          open={showEditModal}
          onClose={() => setShowEditModal(false)}
          speaker={editingSpeaker}
          onSave={async (u) => { await saveEditedSpeaker(u); setShowEditModal(false) }}
          workspaceId={workspaceId} webId={webId} campaignId={campaignId}
        />
      )}
      {deleteTarget && (
        <DeleteSpeakerModal
          speaker={deleteTarget}
          deleting={deletingParticipant}
          onClose={() => setDeleteTarget(null)}
          onConfirm={async () => { const ok = await deleteSpeakerAndPersist(deleteTarget.ContactId || ''); if (ok) setDeleteTarget(null) }}
        />
      )}
    </>
  )
}
